import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import "@progressive-development/pd-icon/pd-icon";
import { pdIcons } from "@progressive-development/pd-icon";
import { formatShortDate } from "@progressive-development/pd-utils";
//#region src/pd-history/PdHistoryItem.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/** Max changed keys of a structured diff shown without a collapse toggle. */
var INLINE_DIFF_LIMIT = 5;
/** Icon per history kind (marker on the left of each row). */
var kindIcons = {
	created: pdIcons.ICON_ADD,
	updated: pdIcons.ICON_EDIT_NEW,
	deleted: pdIcons.ICON_DELETE_NEW,
	restored: pdIcons.ICON_ARROW_BACK,
	process: pdIcons.ICON_SETTINGS
};
var PdHistoryItem = class PdHistoryItem extends LitElement {
	constructor(..._args) {
		super(..._args);
		this._showChanges = false;
		this._expandedFields = /* @__PURE__ */ new Set();
	}
	static {
		this.styles = css`
    :host {
      display: block;
      container-type: inline-size;
    }

    .row {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.6rem 0;
    }

    /* Kind marker: icon in a soft-tinted circle, color per kind */
    .kind-marker {
      flex: none;
      width: var(--pd-history-marker-size, 32px);
      height: var(--pd-history-marker-size, 32px);
      border-radius: 50%;
      display: grid;
      place-items: center;
      background: color-mix(in srgb, var(--_kind-col) 15%, transparent);
      --pd-icon-col: var(--_kind-col);
      --pd-icon-size: calc(var(--pd-history-marker-size, 32px) * 0.55);
    }

    .kind--created {
      --_kind-col: var(
        --pd-history-kind-created-col,
        var(--pd-default-success-col, #2e7d32)
      );
    }
    .kind--updated {
      --_kind-col: var(
        --pd-history-kind-updated-col,
        var(--pd-default-info-col, #1565c0)
      );
    }
    .kind--deleted {
      --_kind-col: var(
        --pd-history-kind-deleted-col,
        var(--pd-default-error-col, #c62828)
      );
    }
    .kind--restored {
      --_kind-col: var(
        --pd-history-kind-restored-col,
        var(--pd-secondary-col, #00897b)
      );
    }
    .kind--process {
      --_kind-col: var(
        --pd-history-kind-process-col,
        var(--pd-default-font-muted-col, #757575)
      );
    }

    .body {
      flex: 1;
      min-width: 0;
    }

    .title-line {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .title {
      font-weight: 600;
      color: var(--pd-default-font-title-col, inherit);
    }

    time,
    .actor-line,
    .message {
      font-size: var(--pd-history-font-size-sub, 0.85em);
      color: var(--pd-default-font-muted-col, #757575);
    }

    time {
      white-space: nowrap;
    }

    .message {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .empty {
      font-style: italic;
    }

    /* Expandable diff */
    button {
      font: inherit;
      border: none;
      background: none;
      padding: 0.25rem 0;
      margin: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      cursor: pointer;
      color: var(--pd-default-font-link-col, inherit);
      font-size: var(--pd-history-font-size-sub, 0.85em);
      min-height: 2rem;
    }

    button:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col, currentColor);
      outline-offset: 2px;
    }

    .chevron {
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(90deg);
    }

    .diff {
      display: grid;
      grid-template-columns: max-content 1fr;
      gap: 0.25rem 1rem;
      margin: 0.25rem 0 0.25rem;
      font-size: var(--pd-history-font-size-sub, 0.85em);
    }

    .field-name {
      color: var(--pd-default-font-muted-col, #757575);
    }

    .field-value {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .old {
      color: var(--pd-default-font-muted-col, #757575);
      text-decoration: line-through;
    }

    .object-details {
      margin: 0.25rem 0 0.25rem 0.75rem;
      display: grid;
      grid-template-columns: max-content 1fr;
      gap: 0.15rem 0.75rem;
    }

    /* Narrow containers (mobile / slim app boxes): stack instead of columns */
    @container (max-width: 480px) {
      .title-line time {
        flex-basis: 100%;
      }

      .diff {
        grid-template-columns: 1fr;
        gap: 0.1rem;
      }

      .field-name {
        margin-top: 0.35rem;
      }
    }
  `;
	}
	render() {
		const entry = this.entry;
		if (!entry) return nothing;
		return html`
      <div class="row">
        <span class="kind-marker kind--${entry.kind}" aria-hidden="true">
          <pd-icon icon="${kindIcons[entry.kind]}"></pd-icon>
        </span>
        <div class="body">
          <div class="title-line">
            <span class="title">${this._title(entry)}</span>
            <time datetime="${entry.at}" title="${entry.at}">
              ${formatShortDate(entry.at, true)}
            </time>
          </div>
          ${this._renderActorLine(entry)}
          ${entry.message ? html`<div class="message" title="${entry.message}">
                  ${entry.message}
                </div>` : nothing}
          ${this._renderChanges(entry)}
        </div>
      </div>
    `;
	}
	/** Localized kind label, extended by the mapped action label if present. */
	_title(entry) {
		const kind = this._kindLabel(entry.kind);
		const action = entry.action ? this.actionLabels?.[entry.action] : void 0;
		return action ? `${kind} · ${action}` : kind;
	}
	_kindLabel(kind) {
		switch (kind) {
			case "created": return msg("Angelegt", { id: "pd.history.kind.created" });
			case "updated": return msg("Bearbeitet", { id: "pd.history.kind.updated" });
			case "deleted": return msg("Gelöscht", { id: "pd.history.kind.deleted" });
			case "restored": return msg("Wiederhergestellt", { id: "pd.history.kind.restored" });
			case "process": return msg("Vorgang", { id: "pd.history.kind.process" });
		}
	}
	_renderActorLine(entry) {
		const app = entry.app ? this.appLabels?.[entry.app] ?? entry.app : "";
		return html`
      <div class="actor-line">
        <span title="${entry.userId ?? ""}">${this._actorText(entry)}</span>
        ${app ? html`<span> · ${app}</span>` : nothing}
      </div>
    `;
	}
	/** Actor display: actorLabel → shortened userId (full UID in title) → dash. */
	_actorText(entry) {
		if (entry.actorLabel) return entry.actorLabel;
		if (entry.userId) return entry.userId.length > 8 ? `${entry.userId.slice(0, 8)}…` : entry.userId;
		return "—";
	}
	_renderChanges(entry) {
		const changes = entry.changes ? Object.entries(entry.changes) : [];
		if (changes.length === 0) return nothing;
		const count = changes.length;
		const toggleText = count === 1 ? msg("1 Änderung", { id: "pd.history.changes.one" }) : msg(str`${count} Änderungen`, { id: "pd.history.changes.many" });
		return html`
      <button
        aria-expanded="${this._showChanges}"
        aria-controls="changesId"
        @click="${() => this._showChanges = !this._showChanges}"
      >
        <span
          class="chevron ${this._showChanges ? "open" : ""}"
          aria-hidden="true"
          >▸</span
        >
        ${toggleText}
      </button>
      ${this._showChanges ? html`
              <div class="diff" id="changesId">
                ${changes.map(([field, change]) => html`
                    <span class="field-name">${this._fieldLabel(field)}</span>
                    <span class="field-value">
                      ${this._renderChange(field, change)}
                    </span>
                  `)}
              </div>
            ` : nothing}
    `;
	}
	_fieldLabel(field) {
		return this.fieldLabels?.[field] ?? field;
	}
	/** Primitive changes render inline "old → new", structured ones per key. */
	_renderChange(field, change) {
		if (!this._isComplex(change.old) && !this._isComplex(change.new)) return html`
        <span class="old">${this._renderValue(field, change.old)}</span>
        →
        <span>${this._renderValue(field, change.new)}</span>
      `;
		return this._renderStructuredChange(field, change);
	}
	/**
	* Structured change (object/array): one "old → new" row per CHANGED key —
	* full listings would hide the change among the unchanged entries. A
	* missing side counts as empty, so a first save reads "empty → value"
	* per key. Few rows show directly, many collapse behind a toggle.
	*/
	_renderStructuredChange(field, change) {
		const oldMap = this._toRecord(change.old);
		const newMap = this._toRecord(change.new);
		const changedKeys = [.../* @__PURE__ */ new Set([...Object.keys(newMap), ...Object.keys(oldMap)])].filter((key) => JSON.stringify(oldMap[key]) !== JSON.stringify(newMap[key]));
		if (changedKeys.length <= INLINE_DIFF_LIMIT) return this._renderKeyDiff(field, changedKeys, oldMap, newMap);
		const expanded = this._expandedFields.has(field);
		const count = changedKeys.length;
		return html`
      <button
        aria-expanded="${expanded}"
        @click="${() => this._toggleField(field)}"
      >
        <span class="chevron ${expanded ? "open" : ""}" aria-hidden="true"
          >▸</span
        >
        ${msg(str`${count} Details`, { id: "pd.history.changes.details" })}
      </button>
      ${expanded ? this._renderKeyDiff(field, changedKeys, oldMap, newMap) : nothing}
    `;
	}
	_renderKeyDiff(field, keys, oldMap, newMap) {
		return html`
      <div class="object-details">
        ${keys.map((key) => html`
            <span class="field-name">${this._fieldLabel(key)}</span>
            <span class="field-value">
              <span class="old">
                ${this._renderValue(`${field}.${key}`, oldMap[key])}
              </span>
              →
              <span>${this._renderValue(`${field}.${key}`, newMap[key])}</span>
            </span>
          `)}
      </div>
    `;
	}
	/** Objects pass through, arrays get 1-based index keys, all else is empty. */
	_toRecord(value) {
		if (Array.isArray(value)) return Object.fromEntries(value.map((v, i) => [String(i + 1), v]));
		if (this._isComplex(value)) return value;
		return {};
	}
	/** Formatting order: app valueFormatter → localized "empty" → String(). */
	_renderValue(field, value) {
		const custom = this.valueFormatter?.(field, value);
		if (custom !== void 0) return html`<span>${custom}</span>`;
		if (value === null || value === void 0 || value === "") return html`<span class="empty">
        ${msg("leer", { id: "pd.history.value.empty" })}
      </span>`;
		if (this._isComplex(value)) return html`<span>${JSON.stringify(value)}</span>`;
		return html`<span>${String(value)}</span>`;
	}
	_isComplex(value) {
		return value !== null && typeof value === "object";
	}
	_toggleField(field) {
		const next = new Set(this._expandedFields);
		if (next.has(field)) next.delete(field);
		else next.add(field);
		this._expandedFields = next;
	}
};
__decorate([property({ attribute: false })], PdHistoryItem.prototype, "entry", void 0);
__decorate([property({ attribute: false })], PdHistoryItem.prototype, "actionLabels", void 0);
__decorate([property({ attribute: false })], PdHistoryItem.prototype, "fieldLabels", void 0);
__decorate([property({ attribute: false })], PdHistoryItem.prototype, "appLabels", void 0);
__decorate([property({ attribute: false })], PdHistoryItem.prototype, "valueFormatter", void 0);
__decorate([state()], PdHistoryItem.prototype, "_showChanges", void 0);
__decorate([state()], PdHistoryItem.prototype, "_expandedFields", void 0);
PdHistoryItem = __decorate([localized()], PdHistoryItem);
//#endregion
export { PdHistoryItem };
